import React, {useEffect, useMemo, useState} from "react";
import Field from "../Field.tsx";
import SmartIcon from "../SmartIcon.tsx";
import {SeriesTermInfo} from "../../models/interfaces/series.interface.ts";
import iso6391 from "iso-639-1";
import LanguageInput from "../CreateProjectModal/LanguageInput.tsx";
import useSystemStore from "../../store/useSystemStore.ts";
import {snakeCaseToSentenceCase, titleCase} from "../../utils/utils.ts";
import Select from "../Select.tsx";
import {IItemsSelect} from "../../models/interfaces/selectItems.interface.ts";
import {seriesTermsTagTypes} from "../../models/constants/seriesTermsTagTypes.ts";
import {TiArrowSortedDown, TiArrowSortedUp} from "react-icons/ti";
import NumericFieldArrows from "../NumericFieldArrows.tsx";

interface SeriesTermExpendedDetailsProps {
  isOpen: boolean;
  term: SeriesTermInfo;
  editTerm: (termId: string, value: string, field: "term" | "sourceLanguage" | "tagType" | "complexityScore" | "meaning") => void;
  setTermSynonyms: (termId: string, synonyms: string[]) => void;
  setTermTranslationsKey: (termId: string, oldTargetLanguage: string, newTargetLanguage: string) => void
  setTermTranslations: (termId: string, targetLanguage: string, translation: string) => void;
  removeTermTranslations: (termId: string, targetLanguage: string) => void
}

const tagTypeSelectItems: IItemsSelect[] = seriesTermsTagTypes.map((tagType: string) => {
  return {
    id: tagType,
    title: snakeCaseToSentenceCase(tagType)
  }
})


const SeriesTermExpendedDetails: React.FC<SeriesTermExpendedDetailsProps> = (
  {
    isOpen,
    term,
    editTerm,
    setTermSynonyms,
    setTermTranslationsKey,
    setTermTranslations,
    removeTermTranslations
  }: SeriesTermExpendedDetailsProps
) => {
  const {supportedCodeLanguages} = useSystemStore();

  const [isLanguageInputOpen, setIsLanguageInputOpen] = useState<boolean>(false);
  const [languageInputValue, setLanguageInputValue] = useState<string>(iso6391.getName(term.sourceLanguage));
  const [termTranslationLanguageOpen, setTermTranslationLanguageOpen] = useState<Record<string, boolean>>({});
  const [termTranslationLanguageInput, setTermTranslationLanguageInput] = useState<Record<string, string>>({});

  const languagesName: string[] = useMemo(() => {
    return supportedCodeLanguages.filter((langCode: string) => !Object.keys(term.translations).includes(langCode)).map((langCode: string) => iso6391.getName(langCode));
  }, [term.translations, supportedCodeLanguages]);


  const handleLanguageSelection = (oldCode: string, newLangName: string) => {
    const newCode: string = iso6391.getCode(newLangName);

    // 1. Update the reducer
    editTerm(term.termId, newCode, "sourceLanguage");
    // 2. Update the language input value
    setLanguageInputValue(newLangName);
  };

  useEffect(() => {
    Object.keys(term.translations).map((languagesCode: string) => {
      const languageName: string = iso6391.getName(languagesCode);
      setTermTranslationLanguageOpen((prev: Record<string, boolean>) => ({
        ...prev,
        [languagesCode]: false
      }));
      setTermTranslationLanguageInput((prev: Record<string, string>) => ({
        ...prev,
        [languagesCode]: languageName
      }));
    })
  }, [term.translations, setTermTranslationLanguageOpen, setTermTranslationLanguageInput]);

  return (
    <div
      className={`pl-10 text-sm text-n-300 overflow-hidden transition-all duration-950 ease-in-out transform-gpu origin-top ${
        isOpen ? 'max-h-[2000px] opacity-100 py-2' : 'max-h-0 opacity-0 py-0'
      }`}
    >
      <div className="flex flex-col items-center justify-between w-full pointer-events-auto gap-4 pr-1">
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="w-1/2">
            <Field
              label="Term"
              placeholder="Term Name"
              value={term.term}
              onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => editTerm(term.termId, event.target.value, "term")}
              required
            />
          </div>
          <div className="w-1/2 ">
            <LanguageInput
              isOpen={isLanguageInputOpen}
              setIsOpen={(isOpen: boolean) => setIsLanguageInputOpen(isOpen)}
              languages={languagesName}
              selectedLanguage={languageInputValue}
              onSelectLanguage={(lang: string) => {
                handleLanguageSelection(term.sourceLanguage, lang);
              }}
              onChange={(value: string) => setLanguageInputValue(value)}
              placeholder="Select language"
              label="Source Language"
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="w-1/2">
            <Select
              className="w-full "
              classButton="h-13 rounded-xl shadow-[inset_0_0_0_0.0625rem_#DADBDC] caption1 dark:shadow-[inset_0_0_0_0.0625rem_#2A2E2F] dark:bg-transparent"
              classOptions="min-w-full overflow-auto max-h-40"
              classIcon="w-5 h-5 fill-n-400/50"
              classArrow="fill-n-400"
              label="Tag Type"
              placeholder="Tag Type"
              items={tagTypeSelectItems}
              value={{
                id: term.tagType,
                title: snakeCaseToSentenceCase(term.tagType)
              }}
              onChange={(selectedItem: IItemsSelect) => {
                editTerm(term.termId, selectedItem.id, "tagType");
              }}
            />
          </div>
          <div className="w-1/2 relative">
            <Field
              className="appearance-auto"
              classInput="appearance-auto"
              label="Complexity Score"
              placeholder="Complexity Score"
              type="number"
              value={term.localizationComplexityScore.toLocaleString()}
              onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                let value: number = parseFloat(event.target.value);
                if (isNaN(value)) value = 0;
                if (value < 0) value = 0;
                if (value > 1) value = 1;
                editTerm(term.termId, value.toString(), "complexityScore");
              }}
              numberTypeMin={0}
              numberTypeMax={1}
              numberTypeStep={0.01}
            />
            <div
              className="absolute right-3 top-2/3 transform -translate-y-1/2 flex flex-col items-center justify-center text-n-400">
              <NumericFieldArrows
                onArrowUpClick={() => {
                  const updatedScore: number = Math.min(1, term.localizationComplexityScore + 0.01);
                  editTerm(term.termId, updatedScore.toString(), "complexityScore");
                }}
                onArrowDownClick={() => {
                  const updatedScore: number = Math.max(0, term.localizationComplexityScore - 0.01);
                  editTerm(term.termId, updatedScore.toString(), "complexityScore");
                }
                }
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col w-full">
        <div className="flex items-center justify-between w-full">
          <div className="my-6 text-md font-semibold">Synonyms</div>
          <div
            className="py-1 px-2 text-sm border border-n-400 rounded-xl hover:border-brandPurple-500 hover:text-brandPurple-500 cursor-pointer active:scale-95 transition-transform"
            onClick={() => {
              const updatedSynonyms = [...term.synonyms, ""];
              setTermSynonyms(term.termId, updatedSynonyms);
            }}
          >
            + Add
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {term.synonyms.map((synonym: string, idx: number) => (
            <div
              key={idx}
              className="flex items-center justify-between w-full gap-4 p-2 bg-n-700 rounded-lg"
            >
              <Field
                className="w-full"
                placeholder={`Synonym#${idx + 1}`}
                value={titleCase(synonym)}
                onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  const updatedSynonyms = [...term.synonyms];
                  updatedSynonyms[idx] = event.target.value;
                  setTermSynonyms(term.termId, updatedSynonyms);
                }}
              />
              <div className="cursor-pointer" onClick={() => {
                const updatedSynonyms = term.synonyms.filter((_, index) => index !== idx);
                setTermSynonyms(term.termId, updatedSynonyms);
              }}>
                <SmartIcon
                  name="delete-chat"
                  className="w-5 h-5 fill-bloodCrimson-400/80 hover:fill-bloodCrimson-400"
                />
              </div>
            </div>
          ))}
        </div>
      </div>


      <div className="flex flex-col w-full">
        <div className="flex items-center justify-between w-full">
          <div className="my-6 text-md font-semibold">Translations</div>
          <div
            className="py-1 px-2 text-sm border border-n-400 rounded-xl hover:border-brandPurple-500 hover:text-brandPurple-500 cursor-pointer active:scale-95 transition-transform"
            onClick={() => {
              const newLanguage: string = languagesName[0];
              if (newLanguage) {
                const newCode: string = iso6391.getCode(newLanguage);
                setTermTranslationsKey(term.termId, term.sourceLanguage, newCode);
                setTermTranslations(term.termId, "", "");
                setTermTranslationLanguageOpen(prev => ({
                  ...prev,
                  [newCode]: true
                }));
                setTermTranslationLanguageInput(prev => ({
                  ...prev,
                  [newCode]: newLanguage
                }));
              }
            }}
          >
            + Add
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {Object.keys(term.translations).map((targetLanguage: string, idx: number) => (
            <div
              key={idx}
              className="flex items-center justify-between w-full p-2 bg-n-700 rounded-lg gap-2"
            >
              <LanguageInput
                isOpen={termTranslationLanguageOpen[targetLanguage] ?? false}
                setIsOpen={(isOpen: boolean) => setTermTranslationLanguageOpen(prev => ({
                  ...prev,
                  [targetLanguage]: isOpen
                }))}
                languages={languagesName}
                selectedLanguage={termTranslationLanguageInput[targetLanguage] ?? iso6391.getName(targetLanguage)}
                onSelectLanguage={(lang: string) => {
                  const newCode: string = iso6391.getCode(lang);
                  setTermTranslationsKey(term.termId, targetLanguage, newCode);
                  setTermTranslationLanguageInput(prev => ({
                    ...prev,
                    [targetLanguage]: lang
                  }));
                  setTermTranslationLanguageOpen(prev => ({
                    ...prev,
                    [targetLanguage]: false
                  }));
                }}
                onChange={(value: string) => {
                  setTermTranslationLanguageInput(prev => ({
                    ...prev,
                    [targetLanguage]: value
                  }));
                }}
                placeholder="Select language"
                label="Source Language"
              />
              <Field
                className="w-full"
                label="Translation"
                placeholder={`Synonym#${idx + 1}`}
                value={term.translations[targetLanguage]}
                onChange={
                  (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setTermTranslations(term.termId, targetLanguage, event.target.value);
                  }
                }
              />
              <div className="cursor-pointer mt-6" onClick={() => {
                removeTermTranslations(term.termId, targetLanguage);
                setTermTranslationLanguageOpen((prev: Record<string, boolean>) => {
                  const newState = {...prev};
                  delete newState[targetLanguage];
                  return newState;
                });
                setTermTranslationLanguageInput((prev: Record<string, string>) => {
                  const newState = {...prev};
                  delete newState[targetLanguage];
                  return newState;
                });
              }}>
                <SmartIcon
                  name="delete-chat"
                  className="w-5 h-5 fill-bloodCrimson-400/80 hover:fill-bloodCrimson-400"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SeriesTermExpendedDetails;